import { PrincipalType } from '@treadinc/horizon-api-spec';

import {
  MessagingChannel,
  MessagingMessage,
  principalId,
} from '~hooks/useMessaging/models';
import { isChannelOrganizer } from '~pages/Messages/messagingComposerPolicy';
import { Nullable } from '~types/Nullable';

type MessageActionContext = {
  canModerate: boolean;
  channel: MessagingChannel;
  message: MessagingMessage;
  viewerUserId: Nullable<string>;
};

/** Fails closed: a non-user sender or an unhydrated viewer answers false. */
const isMessageSender = (message: MessagingMessage, viewerUserId: Nullable<string>) =>
  Boolean(viewerUserId) &&
  message.sender.type === PrincipalType.USER &&
  principalId(message.sender) === viewerUserId;

/** Sender-only: `reply_mode` is ignored so a later `one_way` flip freezes nothing. */
export const canEditMessage = ({
  channel,
  message,
  viewerUserId,
}: MessageActionContext) => {
  // The editor only touches text, so an attachment-only message has nothing to edit.
  const hasEditableText = (message.content ?? '').length > 0;

  return (
    hasEditableText &&
    !channel.isArchived &&
    !message.isDeleted &&
    isMessageSender(message, viewerUserId)
  );
};

/** Archiving closes the sender's own delete, but never moderation. */
export const canDeleteMessage = ({
  canModerate,
  channel,
  message,
  viewerUserId,
}: MessageActionContext) => {
  if (message.isDeleted) {
    return false;
  }

  if (isMessageSender(message, viewerUserId)) {
    return !channel.isArchived;
  }

  return canModerate || isChannelOrganizer(channel, viewerUserId);
};
